import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import { movies } from "@/mocks/movies";
import { getCategoryMeta, regionOptions, sortOptions } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 8;

type SortKey = (typeof sortOptions)[number]["key"];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const meta = getCategoryMeta(type);

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [minRating, setMinRating] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: meta
      ? `【${meta.label}】频道 - 棒棒TV精选影视作品`
      : "分类浏览 - 棒棒TV",
    description: meta
      ? meta.description
      : "棒棒TV影视分类频道，浏览电影、电视剧、综艺与纪录片的精选内容。",
    keywords: meta
      ? `${meta.label},棒棒TV,${meta.label}推荐,影视资讯,${meta.label}精选`
      : "棒棒TV,影视分类,影视资讯",
  });

  const categoryMovies = useMemo(
    () => movies.filter((movie) => movie.type === type),
    [type]
  );

  const years = useMemo(
    () =>
      Array.from(new Set(categoryMovies.map((movie) => movie.year))).sort(
        (a, b) => b - a
      ),
    [categoryMovies]
  );

  const filtered = useMemo(() => {
    const result = categoryMovies.filter((movie) => {
      const genreOk =
        selectedGenres.length === 0 ||
        movie.genres.some((genre) => selectedGenres.includes(genre));
      const regionOk =
        selectedRegions.length === 0 || selectedRegions.includes(movie.region);
      const yearOk =
        selectedYears.length === 0 || selectedYears.includes(movie.year);
      const ratingOk = movie.rating >= minRating;
      return genreOk && regionOk && yearOk && ratingOk;
    });

    return result.sort((a, b) => {
      if (sortKey === "rating") return b.rating - a.rating;
      if (sortKey === "hot") return b.hotness - a.hotness;
      return b.year - a.year;
    });
  }, [categoryMovies, selectedGenres, selectedRegions, selectedYears, minRating, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setMinRating(1);
    setPage(1);
  };

  const toggle = <T,>(list: T[], value: T, setter: (next: T[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    setPage(1);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 px-4 py-8 md:px-8 md:py-10">
        {!meta ? (
          <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
            <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-base text-foreground-600">未找到该分类，请从导航重新进入。</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        ) : (
          <>
            <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-accent-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">{meta.label}</span>
            </nav>

            <div className="mb-6 border-l-4 border-accent-500 pl-4">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {meta.label}频道
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
                {meta.description}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="min-w-0">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-foreground-500">
                    共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品
                  </p>
                  <div className="flex items-center gap-1 rounded-full border border-background-300 px-1 py-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => {
                          setSortKey(option.key);
                          setPage(1);
                        }}
                        className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
                          sortKey === option.key
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {pageItems.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
                    <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                    <p className="mt-4 text-base text-foreground-600">
                      未找到符合条件的影视内容
                    </p>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i>
                      重置筛选条件
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      data-product-shop
                      className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4"
                    >
                      {pageItems.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                    <Pagination
                      current={currentPage}
                      total={totalPages}
                      onChange={setPage}
                    />
                  </>
                )}
              </div>

              <aside className="min-w-0">
                <FilterPanel
                  genres={meta.genres}
                  regions={regionOptions}
                  years={years}
                  selectedGenres={selectedGenres}
                  selectedRegions={selectedRegions}
                  selectedYears={selectedYears}
                  minRating={minRating}
                  onToggleGenre={(value) => toggle(selectedGenres, value, setSelectedGenres)}
                  onToggleRegion={(value) => toggle(selectedRegions, value, setSelectedRegions)}
                  onToggleYear={(value) => toggle(selectedYears, value, setSelectedYears)}
                  onSetRating={(value) => {
                    setMinRating(value);
                    setPage(1);
                  }}
                  onReset={resetFilters}
                />
              </aside>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}